import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { getDetailedEntries } from "@/lib/detailedEntryLogger";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { UndoButton } from "./UndoButton";
import { RedoButton } from "./RedoButton";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface MetricInfo {
  key: string;
  name: string;
  emoji: string;
  unit: string;
  isBoolean?: boolean;
}

interface AllEntriesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  entryDate: string; // ISO format YYYY-MM-DD
  enabledMetrics: MetricInfo[];
}

interface DetailedEntry {
  id: string;
  entry_text: string;
  entry_time: string;
  metric_key: string;
  metric_name: string;
  metric_emoji: string;
}

export const AllEntriesModal = ({
  open,
  onOpenChange,
  userId,
  entryDate,
  enabledMetrics,
}: AllEntriesModalProps) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const queryClient = useQueryClient();

  // Refetch entries when undo/redo operations complete
  useEffect(() => {
    const handleUndoRedo = () => {
      if (open) {
        queryClient.invalidateQueries({ queryKey: ["all-detailed-entries", userId, entryDate] });
      }
    };

    window.addEventListener('undo-operation-cleared', handleUndoRedo);
    window.addEventListener('redo-operation-cleared', handleUndoRedo);
    return () => {
      window.removeEventListener('undo-operation-cleared', handleUndoRedo);
      window.removeEventListener('redo-operation-cleared', handleUndoRedo);
    };
  }, [open, userId, entryDate, queryClient]);

  // Fetch all detailed entries for the given date across all metrics
  const { data: allEntries = [], isLoading } = useQuery({
    queryKey: ["all-detailed-entries", userId, entryDate, enabledMetrics.map(m => m.key).join(',')],
    queryFn: async () => {
      // Fetch entries for all enabled metrics in parallel
      const entriesPromises = enabledMetrics.map(async (metric) => {
        const entries = await getDetailedEntries({
          userId,
          entryDate,
          metricKey: metric.key,
        });
        
        // Add metric info to each entry
        return entries.map(entry => ({
          ...entry,
          metric_key: metric.key,
          metric_name: metric.name,
          metric_emoji: metric.emoji,
        }));
      });

      const allEntriesArrays = await Promise.all(entriesPromises);
      const flatEntries = allEntriesArrays.flat();

      // Sort all entries by time (newest first)
      return flatEntries.sort((a, b) => 
        new Date(b.entry_time).getTime() - new Date(a.entry_time).getTime()
      );
    },
    enabled: open && enabledMetrics.length > 0,
  });

  // Control visibility for slide-in animation
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [open]);

  const formatEntryTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Guard against invalid dates
  if (!open || !entryDate || !userId) return null;

  const formattedDate = format(new Date(entryDate + "T00:00:00"), "MMMM d, yyyy");

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={() => onOpenChange(false)}
          />

          {/* Bottom Sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[101] bg-background rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border/50">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-2xl">📋</span>
                <div className="flex-1 min-w-0">
                  <div className="text-lg font-semibold truncate">{t('entries.todays_activity' as any)}</div>
                  <div className="text-sm text-muted-foreground">
                    {formattedDate}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <UndoButton userId={userId} />
                <RedoButton userId={userId} />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Scrollable entries list */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: isVisible ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="flex-1 overflow-y-auto space-y-2 p-4"
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <MiniGridLoader className="w-6 h-6 text-muted-foreground" />
                </div>
              ) : allEntries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <p className="text-sm text-muted-foreground">{t('entries.no_detailed_entries' as any)}</p>
                </div>
              ) : (
                <AnimatePresence mode="sync">
                  {allEntries.map((entry, index) => (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        duration: 0.3,
                        delay: index * 0.03,
                        ease: "easeOut",
                      }}
                      className="group"
                    >
                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                        className="p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors border border-border/50"
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-lg mt-0.5 flex-shrink-0">
                            {entry.metric_emoji}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground leading-relaxed break-words">
                              {entry.entry_text}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatEntryTime(entry.entry_time)}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </motion.div>

            {/* Entry count footer */}
            {!isLoading && allEntries.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: isVisible ? 1 : 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="pt-3 border-t border-border/50 text-center text-xs text-muted-foreground pb-4"
              >
                {allEntries.length} {allEntries.length === 1 ? t('entries.entry_singular' as any) : t('entries.entries_plural' as any)} {t('entries.recorded' as any)}
              </motion.div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};
